/**
 * "Khoá Shortcut" — who still calls the AI tools, and the switch that makes
 * a staff code mandatory (workers/kra-tools/src/staff-access.ts).
 *
 * Rollout: leave OFF while phones get their X-Staff-Code header; this list
 * shows which callers still send none. Once only people who have left are
 * in the "không có mã" group, switch ON. Missed someone → switch OFF, live
 * within 30 s, no deploy.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { getShortcutAccess, setShortcutLock } from '../lib/api.js';
import type { AccessStatus, ShortcutAccessResponse, ShortcutAccessRow } from '../types.js';

const CODE_KEY = 'kra-tools:staffCode';

const STATUS_LABEL: Record<AccessStatus, { text: string; cls: string }> = {
  ok: { text: 'Có mã', cls: 'text-emerald-400' },
  none: { text: 'Không có mã', cls: 'text-amber-400' },
  bad: { text: 'Mã sai / đã nghỉ', cls: 'text-accent' },
  unchecked: { text: 'Chưa kiểm tra được', cls: 'text-muted' },
};

/** Paths shown short: /tools/reply-polish → reply-polish. */
function shortPath(p: string): string {
  return p.replace(/^\/tools\//, '').replace(/^\//, '');
}

function ago(ts: number): string {
  const min = Math.round((Date.now() - ts) / 60_000);
  if (min < 1) return 'vừa xong';
  if (min < 60) return `${min} phút trước`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} giờ trước`;
  return `${Math.round(h / 24)} ngày trước`;
}

interface Caller {
  who: string;
  status: AccessStatus;
  count: number;
  last_at: number;
  paths: string[];
}

function groupCallers(rows: ShortcutAccessRow[]): Caller[] {
  const map = new Map<string, Caller>();
  for (const r of rows) {
    const key = `${r.status}|${r.who}`;
    const c = map.get(key) ?? { who: r.who, status: r.status, count: 0, last_at: 0, paths: [] };
    c.count += r.count;
    c.last_at = Math.max(c.last_at, r.last_at);
    if (!c.paths.includes(shortPath(r.path))) c.paths.push(shortPath(r.path));
    map.set(key, c);
  }
  return [...map.values()].sort((a, b) => b.last_at - a.last_at);
}

function readCode(): string {
  try {
    return localStorage.getItem(CODE_KEY) ?? '';
  } catch {
    return '';
  }
}

export function ShortcutLockSection({ token, onError }: { token: string | null; onError: (msg: string) => void }) {
  const [days, setDays] = useState(7);
  const [data, setData] = useState<ShortcutAccessResponse | null>(null);
  const [code, setCode] = useState(readCode);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    getShortcutAccess(token, days)
      .then(setData)
      .catch((err: Error) => onError(err.message));
  }, [token, days, onError]);

  useEffect(load, [load]);

  const callers = useMemo(() => groupCallers(data?.rows ?? []), [data]);
  // Callers a switch-ON would refuse, seen in the last 48 h.
  const wouldBlock = callers.filter(
    (c) => (c.status === 'none' || c.status === 'bad') && Date.now() - c.last_at < 48 * 3600_000,
  );

  async function toggle() {
    if (!data) return;
    const next = !data.lock_on;
    if (!code.trim()) {
      onError('Nhập mã invitation của anh trước (chỉ lưu trên máy này)');
      return;
    }
    if (
      next &&
      wouldBlock.length > 0 &&
      !confirm(`${wouldBlock.length} nguồn gọi trong 48 giờ qua sẽ bị chặn. Vẫn bật khoá?`)
    ) {
      return;
    }
    setBusy(true);
    try {
      await setShortcutLock(token, code.trim(), next);
      try {
        localStorage.setItem(CODE_KEY, code.trim());
      } catch {
        /* private mode — they just retype it */
      }
      setData({ ...data, lock_on: next, lock_updated_at: Date.now() });
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="bg-card rounded-2xl border border-border overflow-hidden">
      <div className="px-5 py-4 border-b border-border flex items-start justify-between gap-4">
        <div>
          <h2 className="text-sm font-semibold">Khoá Shortcut</h2>
          <p className="text-xs text-muted mt-1">
            Bật khoá → shortcut phải gửi header <span className="font-mono">X-Staff-Code</span> = mã invitation còn hiệu
            lực. Thợ nghỉ (thiết bị bị thu hồi) tự mất quyền. Bật/tắt có hiệu lực trong 30 giây, không cần deploy.
          </p>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="bg-card2 border border-border rounded-lg px-3 py-1.5 text-xs shrink-0"
        >
          <option value={1}>Hôm nay</option>
          <option value={7}>7 ngày</option>
          <option value={30}>30 ngày</option>
        </select>
      </div>

      {!data && <p className="px-5 py-6 text-sm text-muted">Đang tải…</p>}

      {data && (
        <>
          <div className="px-5 py-4 flex flex-wrap items-center gap-3 border-b border-border">
            <span className={data.lock_on ? 'text-sm font-semibold text-emerald-400' : 'text-sm font-semibold text-amber-400'}>
              {data.lock_on ? '🔒 Đang khoá' : '🔓 Đang mở (ai cũng gọi được)'}
            </span>
            {data.lock_updated_at && <span className="text-xs text-muted">đổi {ago(data.lock_updated_at)}</span>}
            <div className="flex-1" />
            <input
              type="password"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Mã của anh"
              autoComplete="off"
              className="bg-card2 border border-border rounded-lg px-3 py-1.5 text-xs w-36"
            />
            <button
              disabled={busy}
              onClick={toggle}
              className={
                data.lock_on
                  ? 'text-xs border border-border text-muted hover:text-white rounded-lg px-3 py-1.5 disabled:opacity-40'
                  : 'text-xs border border-accent/50 text-accent hover:bg-accent/10 rounded-lg px-3 py-1.5 disabled:opacity-40'
              }
            >
              {data.lock_on ? 'Mở khoá' : 'Bật khoá'}
            </button>
          </div>

          {!data.airtable_configured && (
            <p className="px-5 py-2 text-xs text-amber-400 border-b border-border">
              Worker chưa có Airtable — mã chưa kiểm tra được (vẫn cho qua). Chỉ shortcut không gửi mã mới bị chặn.
            </p>
          )}
          {!data.lock_on && wouldBlock.length > 0 && (
            <p className="px-5 py-2 text-xs text-amber-400 border-b border-border">
              {wouldBlock.length} nguồn gọi trong 48 giờ qua chưa có mã hợp lệ — thêm mã cho máy đó trước khi bật khoá.
            </p>
          )}

          {callers.length === 0 && <p className="px-5 py-6 text-sm text-muted">Chưa có lần gọi nào.</p>}
          {callers.length > 0 && (
            <ul className="divide-y divide-border max-h-[420px] overflow-y-auto">
              {callers.map((c) => (
                <li key={`${c.status}|${c.who}`} className="px-5 py-2.5 flex items-center gap-3 text-sm">
                  <span className={`text-xs w-28 shrink-0 ${STATUS_LABEL[c.status].cls}`}>
                    {STATUS_LABEL[c.status].text}
                  </span>
                  <span className="flex-1 min-w-0 truncate">{c.who}</span>
                  <span className="hidden md:block text-xs text-muted truncate max-w-[220px]">{c.paths.join(' · ')}</span>
                  <span className="text-xs text-muted w-14 text-right tabular-nums shrink-0">{c.count} lần</span>
                  <span className="text-xs text-muted w-24 text-right shrink-0">{ago(c.last_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
